import { createBrowserRouter, Navigate, useParams } from 'react-router';
import { AppLayout } from './app/AppLayout';
import { RedirectIfOnboarded, RequireFamily, RequireSession } from './app/guards';
import { NotFound } from './app/NotFound';
import { RouteErrorBoundary } from './app/RouteErrorBoundary';
import { RootRedirect } from './app/RootRedirect';

/**
 * The route table (doc 05 › Sitemap). Guards run in order: session → archived account
 * → has a family → active family. Feature screens beyond M4 are added as they land.
 */
export const routes = [
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      // --- Public -------------------------------------------------------------
      { path: '/', element: <RootRedirect /> },
      {
        path: '/welcome',
        lazy: () =>
          import('./features/auth/WelcomePage').then((m) => ({ Component: m.WelcomePage })),
      },
      {
        path: '/sign-up',
        lazy: () =>
          import('./features/auth/WelcomePage').then((m) => ({ Component: m.SignUpPage })),
      },
      {
        path: '/sign-in',
        lazy: () =>
          import('./features/auth/WelcomePage').then((m) => ({ Component: m.SignInPage })),
      },
      {
        path: '/verify-email',
        lazy: () =>
          import('./features/auth/EmailFlowPages').then((m) => ({ Component: m.VerifyEmailPage })),
      },
      {
        path: '/forgot-password',
        lazy: () =>
          import('./features/auth/EmailFlowPages').then((m) => ({
            Component: m.ForgotPasswordPage,
          })),
      },
      {
        path: '/reset-password',
        lazy: () =>
          import('./features/auth/EmailFlowPages').then((m) => ({
            Component: m.ResetPasswordPage,
          })),
      },
      {
        path: '/privacy',
        lazy: () =>
          import('./features/legal/LegalPage').then((m) => ({ Component: m.PrivacyPage })),
      },
      {
        path: '/terms',
        lazy: () => import('./features/legal/LegalPage').then((m) => ({ Component: m.TermsPage })),
      },
      {
        path: '/styles',
        // Lazy: the styleguide (specimens, playground) must not weigh down the real app.
        lazy: () =>
          import('./features/styleguide/StyleguidePage').then((m) => ({
            Component: m.StyleguidePage,
          })),
      },
      {
        path: '/styles/specimens/:id',
        lazy: () =>
          import('./features/styleguide/specimens/SpecimenPage').then((m) => ({
            Component: m.SpecimenPage,
          })),
      },

      {
        // A shared invite link works signed out too: the invite, then sign-up (doc 05).
        // Signed in, it's the join step, whether or not you have a family yet.
        path: '/join/:code',
        lazy: () =>
          import('./features/onboarding/JoinLinkRoute').then((m) => ({
            Component: m.JoinLinkRoute,
          })),
      },

      // --- Signed in ----------------------------------------------------------
      {
        element: <RequireSession />,
        children: [
          {
            path: '/restore-account',
            lazy: () =>
              import('./features/auth/EmailFlowPages').then((m) => ({
                Component: m.RestoreAccountPage,
              })),
          },
          {
            // Only the fork itself redirects once you have a family…
            path: '/start',
            element: <RedirectIfOnboarded />,
            children: [
              {
                index: true,
                lazy: () =>
                  import('./features/onboarding/StartPage').then((m) => ({
                    Component: m.StartPage,
                  })),
              },
            ],
          },
          {
            // …never the create flow: its last step is where you get your invite code,
            // and by then the family exists.
            path: '/start/create',
            lazy: () =>
              import('./features/onboarding/CreateFamilyPage').then((m) => ({
                Component: m.CreateFamilyPage,
              })),
          },
          {
            // Joining is reachable even with a family: people belong to several.
            path: '/start/join',
            lazy: () =>
              import('./features/onboarding/JoinFamilyPage').then((m) => ({
                Component: m.JoinFamilyPage,
              })),
          },

          // --- Inside the app shell --------------------------------------------
          {
            element: <RequireFamily />,
            children: [
              {
                element: <AppLayout />,
                children: [
                  {
                    path: '/home',
                    lazy: () =>
                      import('./features/home/HomePage').then((m) => ({ Component: m.HomePage })),
                  },
                  {
                    path: '/family',
                    lazy: () =>
                      import('./features/family/FamilyPage').then((m) => ({
                        Component: m.FamilyPage,
                      })),
                  },
                  {
                    path: '/family/people/new',
                    lazy: () =>
                      import('./features/people/AddPersonPage').then((m) => ({
                        Component: m.AddPersonPage,
                      })),
                  },
                  {
                    // Every name in the app links here (doc 13).
                    path: '/people/:personId',
                    lazy: () =>
                      import('./features/people/PersonProfilePage').then((m) => ({
                        Component: m.PersonProfilePage,
                      })),
                  },
                  {
                    path: '/family/holidays',
                    lazy: () =>
                      import('./features/family/HolidaysPage').then((m) => ({
                        Component: m.HolidaysPage,
                      })),
                  },
                  {
                    // Someone's wishlist, giver view (doc 18 §3): where claiming happens.
                    path: '/people/:personId/wishlist',
                    lazy: () =>
                      import('./features/giving/WishlistGiverPage').then((m) => ({
                        Component: m.WishlistGiverPage,
                      })),
                  },
                  {
                    path: '/calendar',
                    lazy: () =>
                      import('./features/calendar/CalendarPage').then((m) => ({
                        Component: m.CalendarPage,
                      })),
                  },
                  // Gift lists (doc 18): a giver's own plans.
                  {
                    path: '/gift-lists',
                    lazy: () =>
                      import('./features/giftLists/GiftListsPage').then((m) => ({
                        Component: m.GiftListsPage,
                      })),
                  },
                  {
                    path: '/gift-lists/new',
                    lazy: () =>
                      import('./features/giftLists/GiftListFormPage').then((m) => ({
                        Component: () => <m.GiftListFormPage mode="new" />,
                      })),
                  },
                  {
                    path: '/gift-lists/:id',
                    lazy: () =>
                      import('./features/giftLists/GiftListPage').then((m) => ({
                        Component: m.GiftListPage,
                      })),
                  },
                  {
                    path: '/gift-lists/:id/edit',
                    lazy: () =>
                      import('./features/giftLists/GiftListFormPage').then((m) => ({
                        Component: () => <m.GiftListFormPage mode="edit" />,
                      })),
                  },
                  {
                    path: '/dates/new',
                    lazy: () =>
                      import('./features/calendar/TrackedDatePage').then((m) => ({
                        Component: m.TrackedDatePage,
                      })),
                  },
                  {
                    path: '/dates/:dateId',
                    lazy: () =>
                      import('./features/calendar/TrackedDatePage').then((m) => ({
                        Component: m.TrackedDatePage,
                      })),
                  },
                  {
                    path: '/settings/reminders',
                    lazy: () =>
                      import('./features/settings/RemindersPage').then((m) => ({
                        Component: m.RemindersPage,
                      })),
                  },
                  {
                    path: '/shopping',
                    lazy: () =>
                      import('./features/giving/ShoppingPage').then((m) => ({
                        Component: m.ShoppingPage,
                      })),
                  },
                  // Later milestones fill these in; for now they render the friendly 404.
                  {
                    path: '/notifications',
                    lazy: () =>
                      import('./features/notifications/NotificationsPage').then((m) => ({
                        Component: m.NotificationsPage,
                      })),
                  },
                  {
                    path: '/settings/notifications',
                    lazy: () =>
                      import('./features/notifications/NotificationSettingsPage').then((m) => ({
                        Component: m.NotificationSettingsPage,
                      })),
                  },
                  {
                    // Where a phone's Share sheet lands (doc 10 › Mobile share).
                    path: '/share-target',
                    lazy: () =>
                      import('./features/share/ShareTargetPage').then((m) => ({
                        Component: m.ShareTargetPage,
                      })),
                  },
                  {
                    // Lazy: most deployments have billing off and never load this.
                    path: '/settings/billing',
                    lazy: () =>
                      import('./features/billing/BillingPage').then((m) => ({
                        Component: m.BillingPage,
                      })),
                  },
                  {
                    path: '/ideas',
                    lazy: () =>
                      import('./features/ideas/IdeasPage').then((m) => ({
                        Component: m.IdeasPage,
                      })),
                  },
                  // My Wishlist (doc 15): yours, or one you keep for a managed person.
                  {
                    // Lazy on purpose: most people never open import (doc 05 › Performance).
                    path: '/wishlist/import',
                    lazy: () =>
                      import('./features/import/ImportPage').then((m) => ({
                        Component: m.ImportPage,
                      })),
                  },
                  {
                    path: '/wishlist',
                    lazy: () =>
                      import('./features/wishlist/WishlistPage').then((m) => ({
                        Component: m.WishlistPage,
                      })),
                  },
                  {
                    path: '/wishlist/:personId',
                    lazy: () =>
                      import('./features/wishlist/WishlistPage').then((m) => ({
                        Component: m.WishlistPage,
                      })),
                  },
                  // Ideas for one person live on their profile (doc 13 §5).
                  { path: '/ideas/:personId', element: <IdeasPersonRedirect /> },
                  {
                    path: '/settings',
                    lazy: () =>
                      import('./features/settings/SettingsPage').then((m) => ({
                        Component: m.SettingsPage,
                      })),
                  },
                  {
                    path: '/settings/account',
                    lazy: () =>
                      import('./features/settings/SettingsPage').then((m) => ({
                        Component: m.AccountSettingsPage,
                      })),
                  },
                ],
              },
            ],
          },
        ],
      },

      { path: '*', element: <NotFound /> },
    ],
  },
];

/** `/ideas/:personId` → the profile's ideas section (doc 05, doc 13 §5). */
function IdeasPersonRedirect() {
  const { personId } = useParams();
  return <Navigate to={`/people/${personId}#ideas`} replace />;
}

export const router = createBrowserRouter(routes);
